import { observer } from "mobx-react";

import {
    DND_NEW_TRANSITION,
    spaceForCrossTransition,
    spaceForLeftTransition,
    spaceForRightTransition
} from "pages/editor/lib/timeline";

import {
    MINIMUM_TRANSITION_LENGTH,
    TRANSITION_MEDIA_TYPES,
    DEFAULT_TRANSITION_LENGTH
} from "pages/editor/state/store/transition";
import { useDrop } from "react-dnd";

import { TransitionDropAreaContainer } from "./transitionDropAreas.styles";

/**
 * @summary - Returns available space for a transition, based on any existing transitions for the section.
 * @param {LayerStore} layer
 * @param {SectionStore} section
 * @param {SectionStore} sectionTo Left side
 * @param {SectionStore} sectionFrom Right side
 * @returns {Boolean}
 */
const getAvailableTransitionDuration = (
    layer,
    section,
    sectionTo,
    sectionFrom
) => {
    if (sectionFrom && sectionTo) {
        return spaceForCrossTransition(layer, sectionTo, sectionFrom);
    } else {
        if (sectionTo && !sectionFrom) {
            return spaceForLeftTransition(section);
        }
        if (sectionFrom && !sectionTo) {
            return spaceForRightTransition(section);
        }
    }
};

/**
 * @summary - Drops a transition, adding it to a layer
 * @param {Object} monitor React DND monitor
 * @param {LayerStore} layer
 * @param {String} sectionFrom sectionFrom ID
 * @param {String} sectionTo sectionTo ID
 * @param {SectionStore} section
 * @param {EditorUIStore} ui
 */
const onDropTransition = (
    monitor,
    layer,
    sectionFrom,
    sectionTo,
    section,
    { composition: { defaultTransitionTime } }
) => {
    const itemType = monitor.getItemType();
    const { transitionData } = monitor.getItem();

    const defaultDuration = defaultTransitionTime
        ? defaultTransitionTime
        : DEFAULT_TRANSITION_LENGTH;

    if (itemType === DND_NEW_TRANSITION) {
        let duration = getAvailableTransitionDuration(
            layer,
            section,
            sectionTo,
            sectionFrom
        );
        if (duration > defaultDuration) {
            duration = defaultDuration;
        }

        layer.addTransition({
            ...transitionData,
            duration,
            sectionFrom,
            sectionTo
        });
    }
};

/**
 * @summary Checks whether a transition can be added. A transition can be dropped if:
 * 1. There is no existing transition on the side of the section being added to
 * 2. The section being added to represents an asset of a transitionable type
 * 3. The resulting transition will be larger than the minimum transition width
 *
 * @param {Object} item React DND item
 * @param {LayerStore} layer
 * @param {SectionStore} section
 * @param {String} sectionFrom sectionFrom ID
 * @param {String} sectionTo sectionTo ID
 * @returns {Boolean}
 */
const canDropTransition = (
    { transitionData: { mediaType } },
    layer,
    sectionFrom,
    sectionTo,
    section
) => {
    const existingTransition =
        layer.getTransitionFrom(sectionFrom) ||
        layer.getTransitionTo(sectionTo);

    const invalidType =
        (!section.canTransitionVideo &&
            mediaType === TRANSITION_MEDIA_TYPES.VIDEO) ||
        (!section.canTransitionAudio &&
            mediaType === TRANSITION_MEDIA_TYPES.AUDIO);

    const resultingTransitionTooSmall =
        getAvailableTransitionDuration(layer, section, sectionTo, sectionFrom) <
        MINIMUM_TRANSITION_LENGTH;

    return !(existingTransition || invalidType || resultingTransitionTooSmall);
};

/**
 * @typedef TransitionDropAreaProps
 * @property {LayerStore} layer
 * @property {String} sectionFrom sectionFrom ID
 * @property {String} sectionTo sectionTo ID
 * @property {SectionStore} section section being evaluated for transitions
 * @property {Object} style
 * @property {EditorUIStore} ui
 */

/**
 * @summary - A transition drop area, a drop target dedicated for adding transitions to a composition
 * @param {TransitionDropAreaProps} props
 * @returns {React.Element}
 */
const TransitionDropArea = ({
    layer,
    sectionFrom,
    sectionTo,
    section,
    style,
    ui
}) => {
    const [{ isOver, canDrop }, drop] = useDrop({
        drop: (_, monitor) =>
            onDropTransition(
                monitor,
                layer,
                sectionFrom,
                sectionTo,
                section,
                ui
            ),
        canDrop: (item) =>
            canDropTransition(item, layer, sectionFrom, sectionTo, section),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        }),
        accept: DND_NEW_TRANSITION
    });
    return (
        <TransitionDropAreaContainer
            ref={drop}
            style={{
                ...(isOver && {
                    opacity: 0.65
                }),
                ...(!canDrop && {
                    backgroundColor: `red`
                }),
                ...style
            }}
        />
    );
};

export default observer(TransitionDropArea);
